<template>
  <div id="app">
    <MapView 
      ref="mapView" 
      @location-found="handleLocationFound"
    />
    
    <ControlIsland 
      ref="controlIsland"
      :stats="stats"
      :debug-mode="debugMode"
      @filters-changed="handleFiltersChanged"
      @toggle-debug="toggleDebugMode"
      @toggle-upload="showUploadModal = true"
    />

    <!-- Debug Upload Modal -->
    <div v-if="showUploadModal" class="modal-overlay" @click="showUploadModal = false">
      <div class="modal-content" @click.stop>
        <h2>Debug: Manual Upload</h2>
        <input 
          type="file" 
          @change="handleFileUpload" 
          accept=".csv,.json,.geojson"
          ref="fileInput"
          id="debug-file-upload"
        />
        <label for="debug-file-upload" class="upload-button">
          Choose File
        </label>
        <p class="file-info" v-if="uploadedFileName">{{ uploadedFileName }}</p>
        <button @click="showUploadModal = false" class="close-button">Close</button>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>Loading Australian data...</p>
      </div>
    </div>
  </div>
</template>

<script>
import MapView from './components/MapView.vue'
import ControlIsland from './components/ControlIsland.vue'
import { fetchAustralianData, parseCSV, parseGeoJSON, saveToIndexedDB, loadFromIndexedDB } from './lib/dataParser.js'

export default {
  name: 'App',
  components: {
    MapView,
    ControlIsland
  },
  data() {
    return {
      currentData: null,
      filteredData: null,
      stats: {
        total: 0,
        filtered: 0
      },
      debugMode: false,
      showUploadModal: false,
      uploadedFileName: '',
      loading: false,
      currentFilters: {
        status: 'all',
        beforeDate: '',
        region: 'all'
      }
    }
  },
  async mounted() {
    await this.loadAustralianData();
    this.loadCachedData();
  },
  methods: {
    async loadAustralianData(region = 'all') {
      this.loading = true;
      
      try {
        const data = await fetchAustralianData(region);
        this.currentData = data;
        this.filteredData = data;
        this.updateStats();
        
        await saveToIndexedDB('australian-data', data);
        
        if (this.$refs.mapView) {
          this.$refs.mapView.loadData(data);
        }
      } catch (error) {
        console.error('Error loading Australian data:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadCachedData() {
      try {
        const cachedData = await loadFromIndexedDB('australian-data');
        if (cachedData && !this.currentData) {
          this.currentData = cachedData;
          this.filteredData = cachedData;
          this.updateStats();
          
          if (this.$refs.mapView) {
            this.$refs.mapView.loadData(cachedData);
          }
        }
      } catch (error) {
        console.error('Error loading cached data:', error);
      }
    },

    handleFiltersChanged(filters) {
      const previousRegion = this.currentFilters.region;
      this.currentFilters = filters;
      
      if (filters.region !== previousRegion) {
        this.loadAustralianData(filters.region);
        return;
      }
      
      if (!this.currentData) return;

      this.filteredData = this.filterFeatures(this.currentData, filters);
      this.updateStats();
      
      if (this.$refs.mapView) {
        this.$refs.mapView.updateFilter(this.filteredData);
      }
    },

    filterFeatures(geojson, filters) {
      if (!geojson || !geojson.features) return geojson;

      const filtered = {
        type: 'FeatureCollection',
        features: geojson.features.filter(feature => {
          const props = feature.properties;
          
          // Hide unnamed filter
          if (filters.hideUnnamed) {
            const name = (props.name || '').toLowerCase();
            if (name === 'unnamed location' || name === '' || !props.name) {
              return false;
            }
          }
          
          // Status filter
          if (filters.status !== 'all') {
            const status = (props.status || props.type || '').toLowerCase();
            if (!status.includes(filters.status.toLowerCase())) {
              return false;
            }
          }
          
          // Before date filter
          if (filters.beforeDate) {
            const filterDate = new Date(filters.beforeDate);
            const closureDate = props.closureDate || props.closure_date || '';
            const abandonmentDate = props.abandonmentDate || props.abandonment_date || '';
            
            if (closureDate) {
              const propDate = new Date(closureDate);
              if (propDate > filterDate) return false;
            } else if (abandonmentDate) {
              const propDate = new Date(abandonmentDate);
              if (propDate > filterDate) return false;
            }
          }
          
          return true;
        })
      };

      return filtered;
    },

    updateStats() {
      this.stats = {
        total: this.currentData?.features?.length || 0,
        filtered: this.filteredData?.features?.length || 0
      };
    },

    handleLocationFound(location) {
      console.log('Location found:', location);
    },

    toggleDebugMode() {
      this.debugMode = !this.debugMode;
      console.log('Debug mode:', this.debugMode ? 'ON' : 'OFF');
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.uploadedFileName = file.name;
      const fileExt = file.name.split('.').pop().toLowerCase();

      try {
        let geojson;
        
        if (fileExt === 'csv') {
          geojson = await parseCSV(file);
        } else if (fileExt === 'json' || fileExt === 'geojson') {
          geojson = await parseGeoJSON(file);
        } else {
          alert('Unsupported file format. Please use CSV or GeoJSON.');
          return;
        }

        this.currentData = geojson;
        this.filteredData = geojson;
        this.updateStats();
        
        if (this.$refs.mapView) {
          this.$refs.mapView.loadData(geojson);
        }
        
        this.showUploadModal = false;
      } catch (error) {
        console.error('Error parsing file:', error);
        alert('Error parsing file: ' + error.message);
      }
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  overflow: hidden;
}

#app {
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100vh;
  width: 100vw;
  background: #0a0a0f;
  color: #e0e0e0;
  position: relative;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.modal-content h2 {
  color: rgba(255, 152, 0, 0.9);
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  font-weight: 500;
}

input[type="file"] {
  display: none;
}

.upload-button {
  display: block;
  background: rgba(66, 153, 225, 0.3);
  border: 1px solid rgba(66, 153, 225, 0.5);
  color: white;
  padding: 12px 20px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 500;
  text-align: center;
  margin: 20px 0;
  transition: all 0.2s ease;
}

.upload-button:hover {
  background: rgba(66, 153, 225, 0.4);
  border-color: rgba(66, 153, 225, 0.7);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
}

.file-info {
  color: rgba(66, 185, 131, 0.9);
  font-size: 0.9rem;
  margin: 10px 0;
}

.close-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 10px;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.loading-spinner {
  text-align: center;
  color: #e0e0e0;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top: 4px solid rgba(66, 153, 225, 0.8);
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-spinner p {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
}
</style>
